import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import { inNextServer, getDefaultRadixPortalContainer, getRadixModalModeEnabled, Link } from '@fabric-app/compat';
import { IconUpsellTick, IconUpsellCross } from '@fabric-app/icons/old';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ItemType } from '../pricing.types.js';
import { styled } from 'styled-components';
import { P } from '@fabric-app/ui/components/Typography';
import { mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { Modal, defaultModalContentProps } from '@fabric-app/ui/components/Modal/Modal';
import { Button } from '@fabric-app/ui/components/Button';
import { viewportBreakpoints } from '@fabric-app/ui/constants/breakpoints';
import { marketingItems } from '../pricing.config.js';

const PlanFeature = styled.li.attrs(({ feature, ...p }) => {
  return {
    ...p,
    ["data-feature-type"]: feature.featureType,
    ["data-has"]: feature.has,
    children: /* @__PURE__ */ jsxs(Fragment, {
      children: [
        feature.has ? /* @__PURE__ */ jsx(IconUpsellTick, {}) : /* @__PURE__ */ jsx(IconUpsellCross, {}),
        /* @__PURE__ */ jsx(P, {
          color: "app-primary",
          weight: 600,
          children: feature.description
        })
      ]
    })
  };
}).withConfig({
  displayName: "UpgradePromptModal__PlanFeature",
  componentId: "sc-3366e107-0"
})([
  `display:flex;align-items:center;gap:10px;color:#666;font-size:15px;font-style:normal;font-weight:500;line-height:20px;white-space:nowrap;> svg{flex-shrink:0;width:20px;height:20px;}&[data-has]{color:#30f;}&[data-feature-type='HIGHLIGHTED']{color:#9e00ff;}`
]);
const PlanSeparator = styled.li.attrs((p) => {
  return {
    ...p,
    ["data-item-separator"]: true
  };
}).withConfig({
  displayName: "UpgradePromptModal__PlanSeparator",
  componentId: "sc-3366e107-1"
})([
  `height:1px;align-self:stretch;background:rgba(217,217,217,0.5);`
]);
const mapPlanItem = (item, index) => {
  switch (item.type) {
    case ItemType.Feature:
      return /* @__PURE__ */ jsx(PlanFeature, {
        feature: item
      }, `${index}-${item.description}`);
    case ItemType.Separator:
      return /* @__PURE__ */ jsx(PlanSeparator, {}, index);
  }
};
const Backdrop = styled.div.withConfig({
  displayName: "UpgradePromptModal__Backdrop",
  componentId: "sc-3366e107-2"
})([
  `z-index:125;position:fixed;top:0;left:0;width:100%;height:100%;background-color:rgba(128,128,128,0.3);`,
  `{display:none;&[data-no-bottom-gap='true']{bottom:0;}}`
], mediaMobile);
const Content = styled.div.withConfig({
  displayName: "UpgradePromptModal__Content",
  componentId: "sc-3366e107-3"
})([
  `width:650px;flex-shrink:0;border-radius:32px;background:rgba(var(--fabric-color-bg-primary-rgb),0.95);color:var(--fabric-color-text-primary);align-items:center;z-index:125;overflow-y:auto;padding:18px;gap:17px;display:flex;flex-direction:column;position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);header{display:flex;align-items:center;justify-content:center;width:100%;position:relative;padding:4.5px 0;min-height:33px;}h2{color:var(--fabric-color-text-primary);text-align:center;font-size:24px;font-style:normal;font-weight:700;line-height:normal;letter-spacing:-0.24px;max-width:253px;}`,
  `{top:0;background:rgba(var(--fabric-color-bg-primary-rgb),0.9);bottom:0;left:0;right:0;gap:28px;transform:none;width:auto;height:auto;padding:97px 30px 30px 30px;padding-top:calc(env(safe-area-inset-top,0px) + 2rem);overflow-y:auto;h4{text-align:center;}header{flex-direction:column-reverse;gap:3rem;padding:0;height:max-content;min-height:initial;}padding-bottom:calc(env(safe-area-inset-bottom) + 5rem);&[data-no-bottom-gap='true']{bottom:0;}}@media (min-width:calc(`,
  `px + 1px)) and (max-height:550px){top:0;bottom:0;transform:translateX(-50%);overflow-y:auto;}`
], mediaMobile, viewportBreakpoints.MOBILE);
const LimitBadge = styled.span.withConfig({
  displayName: "UpgradePromptModal__LimitBadge",
  componentId: "sc-3366e107-4"
})([
  `display:inline-flex;padding:4px 10px;flex-direction:column;align-items:flex-start;gap:10px;border-radius:14px;background:rgba(255,0,0,0.1);color:#f00;font-size:14px;font-style:normal;font-weight:500;line-height:normal;@media screen and (min-width:769px){position:relative;left:50%;transform:translateX(-50%);}`
]);
const SubContent = styled.div.withConfig({
  displayName: "UpgradePromptModal__SubContent",
  componentId: "sc-3366e107-5"
})([
  `display:flex;align-items:flex-start;justify-content:space-between;width:100%;gap:32px;padding:10px;margin-top:23px;`,
  `{flex-direction:column-reverse;align-items:center;gap:40px;margin-top:4px;}`
], mediaMobile);
const FeatureList = styled.div.withConfig({
  displayName: "UpgradePromptModal__FeatureList",
  componentId: "sc-3366e107-6"
})([
  `display:flex;width:278px;padding:22px 27px;flex-direction:column;justify-content:center;gap:16px;flex-shrink:0;border-radius:12px;background:var(--fabric-color-bg-primary);box-shadow:0px 4px 64px 0px rgba(128,128,128,0.1);> p{color:var(--fabric-color-text-tertiary);font-size:15px;font-style:normal;font-weight:600;line-height:20px;}> ul{display:flex;flex-direction:column;gap:16px;> li{display:flex;align-items:center;gap:10px;font-weight:600;font-size:15px;line-height:20px;color:#2a37ee;&.purple{color:#9e00ff;}> svg{width:18px;height:18px;}}}`
]);
const Actions = styled.div.withConfig({
  displayName: "UpgradePromptModal__Actions",
  componentId: "sc-3366e107-7"
})([
  `display:flex;flex-direction:column;justify-content:center;align-items:center;height:100%;gap:28px;flex-grow:1;color:rgba(0,0,0,0.7);text-align:center;font-size:15px;font-style:normal;font-weight:600;line-height:normal;padding:55px 31px 0 31px;flex-grow:1;`,
  `{padding:0 31px;}`
], mediaMobile);
const ModalClose = styled(Modal.Close).withConfig({
  displayName: "UpgradePromptModal__ModalClose",
  componentId: "sc-3366e107-8"
})([
  ``
]);
const UpgradePromptModal = ({ title, subtitle, limitBadge, titleMaxWidth, noBottomGap = false, onClose }) => {
  if (inNextServer()) return null;
  return /* @__PURE__ */ jsx(Modal.Root, {
    open: true,
    onOpenChange: onClose,
    children: /* @__PURE__ */ jsxs(Modal.Portal, {
      container: getDefaultRadixPortalContainer(),
      children: [
        getRadixModalModeEnabled() ? /* @__PURE__ */ jsx(DialogPrimitive.Overlay, {
          asChild: true,
          children: /* @__PURE__ */ jsx(Backdrop, {
            "data-no-bottom-gap": noBottomGap,
            onClick: onClose
          })
        }) : /* @__PURE__ */ jsx(Backdrop, {
          "data-no-bottom-gap": noBottomGap,
          onClick: onClose
        }),
        /* @__PURE__ */ jsx(DialogPrimitive.Content, {
          "aria-describedby": void 0,
          ...defaultModalContentProps,
          asChild: true,
          children: /* @__PURE__ */ jsxs(Content, {
            "data-no-bottom-gap": noBottomGap,
            children: [
              /* @__PURE__ */ jsx(VisuallyHidden, {
                children: /* @__PURE__ */ jsx(DialogPrimitive.Title, {
                  children: title
                })
              }),
              /* @__PURE__ */ jsxs("header", {
                children: [
                  limitBadge && /* @__PURE__ */ jsx(LimitBadge, {
                    children: limitBadge
                  }),
                  /* @__PURE__ */ jsx(ModalClose, {
                    as: "button",
                    onClick: onClose
                  })
                ]
              }),
              /* @__PURE__ */ jsx("h2", {
                style: {
                  maxWidth: titleMaxWidth ?? "253px"
                },
                children: title
              }),
              subtitle && /* @__PURE__ */ jsx("h4", {
                children: subtitle
              }),
              /* @__PURE__ */ jsxs(SubContent, {
                children: [
                  /* @__PURE__ */ jsxs(FeatureList, {
                    children: [
                      /* @__PURE__ */ jsx(P, {
                        weight: 600,
                        color: "tertiary",
                        children: "You’ll also get:"
                      }),
                      /* @__PURE__ */ jsx("ul", {
                        children: marketingItems.map(mapPlanItem)
                      })
                    ]
                  }),
                  /* @__PURE__ */ jsxs(Actions, {
                    children: [
                      /* @__PURE__ */ jsx(Button, {
                        onClick: onClose,
                        size: "lg",
                        style: {
                          minWidth: 144
                        },
                        as: Link,
                        href: "/pricing",
                        children: "Upgrade"
                      }),
                      /* @__PURE__ */ jsx(P, {
                        weight: 600,
                        color: "tertiary",
                        children: "Cancel any time."
                      })
                    ]
                  })
                ]
              })
            ]
          })
        })
      ]
    })
  });
};

export { UpgradePromptModal as default };
//# sourceMappingURL=UpgradePromptModal.js.map
